import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { IConfiguration, IHttpErrorHandlerService, NormalizedHttpErrorValueObject } from '@gishub/domain';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const SLOW_REQUEST_MS = 5000;

interface RetryableConfig {
  retryCount?: number;
}

function isRetryableMethod(method?: string): boolean {
  return ['GET', 'HEAD', 'OPTIONS'].includes((method ?? 'GET').toUpperCase());
}

function shouldRetry(status: number | undefined): boolean {
  return status === undefined || status >= 500;
}

export function getHttpErrorMessage(
  status: number | undefined,
  responseData: any,
  code?: string,
  fallbackMessage = 'Ha ocurrido un error inesperado'
): string {
  if (responseData && typeof responseData === 'object' && typeof responseData.message === 'string' && responseData.message.trim()) {
    return responseData.message;
  }
  if (code === 'ECONNABORTED') {
    return 'La solicitud tardó demasiado';
  }
  switch (status) {
    case 400: return 'Solicitud inválida';
    case 401: return 'No autorizado';
    case 403: return 'Acceso denegado';
    case 404: return 'Recurso no encontrado';
    case 408: return 'La solicitud expiró';
    case 422: return 'Error de validación';
    case 429: return 'Demasiadas solicitudes. Intente nuevamente más tarde';
    case 500: return 'Error interno del servidor';
    case 502: return 'Servidor temporalmente no disponible';
    case 503: return 'Servicio no disponible';
    case 504: return 'Tiempo de espera del servidor agotado';
    default: return status !== undefined ? fallbackMessage : 'Error de conexión';
  }
}

function logRequest(method: string, url?: string): void {
  console.log(`[HTTP] → ${method} ${url ?? ''}`);
}

function logResponse(method: string, url: string | undefined, status: number, durationMs: number): void {
  console.log(`[HTTP] ← ${status} ${method} ${url ?? ''} (${durationMs}ms)`);
  if (durationMs > SLOW_REQUEST_MS) {
    console.warn(`[HTTP] ⚠ ${method} ${url ?? ''} tardó ${durationMs}ms`);
  }
}

export class AxiosClient {
  private static instance: AxiosInstance | null = null;
  private static getAccessToken: (() => string | null) | null = null;
  private static errorHandler: IHttpErrorHandlerService | null = null;
  private static isProduction = false;

  static setApiUrl(
    configuration: IConfiguration,
    getAccessToken?: () => string | null,
    errorHandler?: IHttpErrorHandlerService
  ): void {
    AxiosClient.getAccessToken = getAccessToken ?? null;
    AxiosClient.errorHandler = errorHandler ?? null;
    AxiosClient.isProduction = configuration.isProduction;

    const instance = axios.create({
      baseURL: configuration.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use((config) => {
      const token = AxiosClient.getAccessToken?.();
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      (config as unknown as { _startTime?: number })._startTime = Date.now();
      if (!AxiosClient.isProduction) {
        logRequest((config.method ?? 'get').toUpperCase(), config.url);
      }
      return config;
    });

    instance.interceptors.response.use(
      (response) => {
        if (!AxiosClient.isProduction) {
          const startTime = (response.config as unknown as { _startTime?: number })._startTime;
          const durationMs = startTime ? Date.now() - startTime : 0;
          logResponse(
            (response.config.method ?? 'get').toUpperCase(),
            response.config.url,
            response.status,
            durationMs
          );
        }
        return response;
      },
      (error) => {
        const config = error?.config;
        const status: number | undefined = error?.response?.status;
        const method = (config?.method ?? 'get').toUpperCase();
        const attempts = (config as RetryableConfig | undefined)?.retryCount ?? 0;

        if (config && isRetryableMethod(method) && attempts < MAX_RETRIES && shouldRetry(status)) {
          (config as RetryableConfig).retryCount = attempts + 1;
          const delayMs = BASE_DELAY_MS * Math.pow(2, attempts);
          return new Promise<AxiosResponse>((resolve) => {
            setTimeout(() => resolve(instance(config)), delayMs);
          });
        }

        const normalized = new NormalizedHttpErrorValueObject(
          status,
          getHttpErrorMessage(status, error?.response?.data, error?.code),
          error?.config?.url,
          method || undefined,
          error?.code,
        );

        if (!AxiosClient.isProduction) {
          const startTime = (error?.config as unknown as { _startTime?: number })?._startTime;
          const durationMs = startTime ? Date.now() - startTime : 0;
          console.error(`[HTTP] ✗ ${normalized.method} ${normalized.url ?? ''} — ${durationMs}ms`, normalized);
        }

        AxiosClient.errorHandler?.handle(normalized);
        return Promise.reject(normalized);
      }
    );

    AxiosClient.instance = instance;
  }

  static getInstance(): AxiosInstance {
    return AxiosClient.instance!;
  }
}

export const axiosClient = new Proxy({} as AxiosInstance, {
  get(_, prop: string): any {
    const instance = AxiosClient.getInstance();
    return (instance as any)[prop];
  }
}) as AxiosInstance;
